import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, Phone, ArrowRight, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { BrandedHeader } from './BrandedHeader';
import { ComplianceFooter } from './ComplianceFooter';
import { SecureUploadCompanyProfileStatus } from './SecureUploadCompanyProfileStatus';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';

export const BorrowerSelfServiceVerification = () => {
  const navigate = useNavigate();
  const {
    companyProfile,
    loading: profileLoading,
    error: profileError,
    retry: retryProfile
  } = useCompanyProfile();

  const [verifyMethod, setVerifyMethod] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [lastName, setLastName] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  if (profileLoading || profileError || !companyProfile) {
    return (
      <SecureUploadCompanyProfileStatus
        loading={profileLoading}
        onRetry={retryProfile}
      />
    );
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!lastName.trim()) {
      toast.error('Please enter your last name');
      return;
    }

    if (verifyMethod === 'email' && !email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    if (verifyMethod === 'phone' && !phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    setIsVerifying(true);

    try {
      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/verify-borrower-self-service`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: verifyMethod === 'email' ? email.trim() : undefined,
            phone: verifyMethod === 'phone' ? phone.trim() : undefined,
            lastName: lastName.trim()
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Verification failed');
      }

      // Store verification data in sessionStorage (contact-based)
      if (result.success && result.contact) {
        sessionStorage.setItem('borrowerUploadSession', JSON.stringify({
          contact: result.contact,
          loanOfficer: result.loanOfficer,
          company: result.company,
          verifiedAt: new Date().toISOString()
        }));
        navigate('/secureuploads/borrower/upload');
      } else {
        throw new Error('Verification failed. Please try again.');
      }

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      toast.error(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const formatPhoneInput = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
return digits;
}
    if (digits.length <= 6) {
return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
}
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  return (
    <div className="h-screen overflow-y-auto relative bg-gradient-to-br from-gray-50 via-blue-50/30 to-cyan-50/20 flex flex-col">
      {/* Decorative gradient orb */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-full blur-3xl pointer-events-none animate-pulse-subtle" />

      {/* Branded Header */}
      <BrandedHeader
        title="Secure Document Upload"
        subtitle="Upload documents to your loan officer securely"
        companyProfile={companyProfile}
      />

      {/* Main Content */}
      <div className="relative flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <Card className="relative overflow-hidden glass-card border border-white/60 shadow-2xl shadow-blue-900/20 animate-fade-in">
            {/* Decorative gradient overlay */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-full blur-3xl pointer-events-none" />

            <CardHeader className="relative text-center space-y-4 pt-8 border-b border-cyan-200/30 pb-6">
              <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-cyan-900 to-blue-900 bg-clip-text text-transparent">
                Verify Your Identity
              </CardTitle>
              <CardDescription className="text-gray-700">
                Enter your information below to access the secure upload portal
              </CardDescription>
            </CardHeader>

            <CardContent className="relative pt-6 pb-8">
              <form onSubmit={handleVerify} className="space-y-6">
                {/* Verification Method Tabs */}
                <Tabs value={verifyMethod} onValueChange={(v) => setVerifyMethod(v as 'email' | 'phone')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </TabsTrigger>
                    <TabsTrigger value="phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Phone
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="email" className="mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isVerifying}
                        className="h-12"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="phone" className="mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={phone}
                        onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                        disabled={isVerifying}
                        className="h-12"
                      />
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Last Name */}
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={isVerifying}
                    className="h-12"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isVerifying}
                  className="w-full h-12 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/40 transition-all duration-300"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                {/* Help Text */}
                <p className="text-sm text-center text-muted-foreground">
                  Use the email or phone number your loan officer has on file
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Compliance Footer */}
      <ComplianceFooter companyProfile={companyProfile} />
    </div>
  );
};
